/**
 * `X-Fingerprint` request plumbing for web-viewer (PLT-2288).
 *
 * Two exports, because the header reaches the wire by two different routes:
 *
 *  - `attachFingerprint` is folded into the **existing** request interceptor in
 *    `AxiosService.js` (see Key Decision 6 — a separately registered async
 *    interceptor would run *before* the cancel-token one, since axios 0.x runs
 *    request interceptors in reverse registration order, and would delay the
 *    duplicate-request bookkeeping by the fingerprint compute).
 *  - `applyFingerprintInterceptor` registers it on the instance forked by
 *    `railsService.create({})` in `src/api/index.js`, which inherits the shared
 *    instance's config but **not** its interceptors on axios `^0.24`.
 *
 * Nothing here is ever registered on the bare `axios` import. That is how the
 * Firebase cloud-function POST (`src/store/modules/reports/actions.js`) and the
 * Hermes compile POST (`src/services/hermesService.js`) stay out of scope by
 * construction; the origin gate below is the second line of defence, not the
 * first.
 */

import env from '@/utils/config.js'
import { getFingerprint } from '@/utils/fingerprint.js'

/**
 * The header name Rack::Attack reads as `req.env['HTTP_X_FINGERPRINT']`.
 * `spectora`'s CORS config sets `headers: :any` on every resource, so no
 * preflight change is needed to send it.
 */
export const FINGERPRINT_HEADER = 'X-Fingerprint'

/**
 * How long a request will wait for a visitor ID before going out without one.
 *
 * The `try/catch` below covers a **rejection**; it cannot cover
 * **non-resolution**. `config.timeout` is applied as `request.timeout` on the
 * `XMLHttpRequest` inside axios 0.24's adapter (`lib/adapters/xhr.js:47`), and
 * the adapter is only reached *after* the request-interceptor chain resolves —
 * so a request stalled in this interceptor gets no timeout at all: it never
 * dispatches, never rejects, and surfaces no error. In the viewer that is a
 * report stuck loading forever with nothing in the console, on 100% of the
 * app's Rails I/O.
 *
 * A stall is the more likely failure for this library, not a hypothetical:
 * `agent.get()` collects async signals (`OfflineAudioContext`, font
 * enumeration, canvas) and audio-context stalls on some Safari builds are a
 * known hazard. 500 ms also caps the cold-compute cost the holder documents at
 * 100-500 ms on the first request's critical path.
 */
export const FINGERPRINT_TIMEOUT_MS = 500

/**
 * Resolves to the fingerprint, or to `null` once the deadline passes.
 *
 * `null` already means "send no header", so the degraded path needs no new
 * branch. The timer is cleared either way so a settled compute leaves nothing
 * pending behind it.
 */
const withDeadline = (promise) => {
  let timer

  const deadline = new Promise((resolve) => {
    timer = setTimeout(() => resolve(null), FINGERPRINT_TIMEOUT_MS)
  })

  return Promise.race([promise, deadline]).finally(() => clearTimeout(timer))
}

/**
 * Hostnames a base URL resolves to when the domain env var is unset.
 *
 * `AxiosService.js` builds the instance base as `https://${config('RAILS_DOMAIN')}/`,
 * and `config` returns `undefined` for a var that is not set — so an unset
 * `VITE_RAILS_DOMAIN` interpolates to the literal string `https://undefined/`,
 * which parses cleanly and would otherwise match every relative request. That
 * is precisely the case AC 7 says must yield no header, so it is named here
 * rather than left to look like a real origin.
 */
const UNRESOLVABLE_HOSTS = ['undefined', 'null', '']

/**
 * Whether the build has the fingerprint client turned on.
 *
 * Read **lazily**, on every request rather than once into a module constant, so
 * a spec can stub the env per case and so the flag reflects the environment the
 * bundle actually runs in. `VITE_ENABLE_FINGERPRINTING` defaults off: merging
 * this change is inert, and each environment is turned on — and back off —
 * without a revert.
 */
export const fingerprintingEnabled = () =>
  env('ENABLE_FINGERPRINTING') === 'true'

/**
 * The Rails origin a request must be bound for to carry the header, or `null`
 * when no such origin can be derived.
 *
 * Takes the instance's *own* `defaults.baseURL` rather than the per-request
 * `config.baseURL`: the former is fixed to the Rails host at module load, so a
 * request that overrides its base cannot talk this gate into matching itself.
 *
 * Everything unresolvable returns `null`, and `null` means no header. There is
 * deliberately **no fallback to `window.location.origin`** — a fingerprint is a
 * persistent device identifier, so "we could not tell where this is going" must
 * never resolve to "send it anyway" (Key Decision 2, AC 7).
 */
export const railsOriginFrom = (baseURL) => {
  if (typeof baseURL !== 'string' || baseURL === '') return null

  try {
    const { origin, hostname } = new URL(baseURL)

    if (UNRESOLVABLE_HOSTS.includes(hostname)) return null

    return origin
  } catch (error) {
    return null
  }
}

/**
 * Where a request is actually going, or `null` when that cannot be determined.
 *
 * Resolved against the base axios itself will use (`config.baseURL`, falling
 * back to the instance default), so the three shapes that matter all land
 * correctly: an absolute third-party URL resolves to its own origin, a relative
 * URL resolves to the instance's Rails origin, and a relative URL under a
 * per-request base override resolves to *that* override — which then fails the
 * comparison, rather than inheriting a match it did not earn.
 *
 * The instance base carries a trailing slash (`https://host/`), which is why
 * this compares parsed origins and never raw strings (Note 7).
 */
export const resolveRequestOrigin = (requestConfig, instanceBaseURL) => {
  const base = requestConfig.baseURL || instanceBaseURL
  const url = requestConfig.url || ''

  try {
    return new URL(url, base || undefined).origin
  } catch (error) {
    return null
  }
}

/**
 * Attaches `X-Fingerprint` to a request bound for the Rails host.
 *
 * Two gates, both required, and neither one ever blocks or fails a request: a
 * build with the flag off, a request to a third-party host, an underivable
 * Rails origin, or a browser that cannot produce a visitor ID all return the
 * config untouched. The whole body is wrapped so a thrown error can never
 * reject the request, and the wait itself is bounded by
 * `FINGERPRINT_TIMEOUT_MS` so an agent that never settles cannot hold the
 * request either — the header is telemetry, and telemetry does not get to
 * break a report view.
 *
 * **Attachment is on every method, reads included.** This is the one deliberate
 * divergence from the CSRF client, which attaches on writes only: `api/general`
 * and `api/fingerprint` count *every* request, so a write-only gate would leave
 * both rules blind on the traffic that dominates this app (AC 4, Key
 * Decision 1). It is likewise not gated on a session cookie — anonymous buyers
 * and agents reading a shared link are the bulk of viewer traffic, and full
 * coverage is the point (AC 3, Key Decision 8).
 *
 * Mutates and returns `requestConfig`, which is what an axios request
 * interceptor is.
 */
export const attachFingerprint = async (requestConfig, instanceBaseURL) => {
  try {
    if (!fingerprintingEnabled()) return requestConfig

    const railsOrigin = railsOriginFrom(instanceBaseURL)

    if (!railsOrigin) return requestConfig

    const requestOrigin = resolveRequestOrigin(requestConfig, instanceBaseURL)

    if (requestOrigin !== railsOrigin) return requestConfig

    const value = await withDeadline(getFingerprint())

    if (!value) return requestConfig

    // axios 0.24 models `config.headers` as a plain object — there is no
    // `AxiosHeaders#set` to go through.
    requestConfig.headers = requestConfig.headers || {}
    requestConfig.headers[FINGERPRINT_HEADER] = value
  } catch (error) {
    // Deliberately swallowed: no fingerprint is a valid outcome, and a broken
    // one must never turn into a failed request.
  }

  return requestConfig
}

/**
 * Registers the fingerprint attach on an axios instance and returns it.
 *
 * This exists for one axios 0.24 detail: `instance.create()` calls
 * `createInstance(mergeConfig(defaultConfig, instanceConfig))`, which builds a
 * fresh `new Axios(...)`. The **config** is inherited (`baseURL`,
 * `withCredentials`); the **interceptors** are not. `src/api/index.js` forks the
 * shared instance exactly that way, so without this call the fork would send no
 * header at all — a silent blind spot, and a trap for the next request added
 * there. The CSRF work hit this same trap in this same file.
 *
 * The shared instance in `AxiosService.js` deliberately does **not** go through
 * here: its own request interceptor already owns the duplicate-request
 * `CancelToken`, and folding the attach into that handler is what keeps the
 * cancel-token assignment ahead of the fingerprint compute (AC 10). Both paths
 * call the same `attachFingerprint`, so there is one source of truth either way.
 *
 * `instance.defaults.baseURL` is read inside the handler rather than closed over
 * at registration time, so a caller that adjusts the fork's defaults afterwards
 * — `src/api/index.js` sets `timeout` after `create()` — cannot leave this gate
 * reading a stale base.
 */
export const applyFingerprintInterceptor = (instance) => {
  instance.interceptors.request.use((requestConfig) =>
    attachFingerprint(requestConfig, instance.defaults.baseURL)
  )

  return instance
}
